import { webAnalytics as wa } from '@principal/web-analytics';
import { useEffect, useMemo, useState } from 'react';
import { MY_PAY } from '../../search/models/BasicSearchOption';
import Alert, { AlertProps } from '../../shared/components/Alert/Alert';
import Link from '../../shared/models/Link';
import Formatter from '../../util/formatter';
import DisbursementSummary from '../models/DisbursementSummary';
import './DisbursementPicker.css';
import { SearchType, SearchTypeLabel, TabType } from '../../shared/models/WebAnalytics';
import { useHubTab } from '../../shared/hooks/useHubTab';
import { getUniqueDisbursementPaidDates } from '../../util/analyticsEventHelpers';

interface Props {
  disbursements: DisbursementSummary[];
  handleDisbursementClickCallback: (disbursementLink: Link, disbursementContentLink: Link | undefined) => void;
  alert?: AlertProps;
  hidePayMethod?: boolean;
  isOps?: boolean;
  basicSearchOption?: string;
  currentSearchType?: 'basic' | 'advanced';
}

const MAX_SIZE = 10;

const DisbursementPicker: React.FC<Props> = (props: Props) => {
  const { disbursements, alert, hidePayMethod, basicSearchOption, currentSearchType, isOps } = props;
  const hubTab = useHubTab();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [minDisplayIndex, setMinDisplayIndex] = useState(0);

  const uniquePaidDateList = useMemo(() => getUniqueDisbursementPaidDates(disbursements), [disbursements]);

  const rightArrowEnabled = (): boolean => minDisplayIndex < disbursements.length - MAX_SIZE;

  const onRightArrowClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
    event.preventDefault();
    if (rightArrowEnabled()) {
      setMinDisplayIndex(Math.min(disbursements.length - MAX_SIZE, minDisplayIndex + MAX_SIZE - 1));
    }
  };

  const leftArrowEnabled = (): boolean => minDisplayIndex > 0;

  const onLeftArrowClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
    event.preventDefault();
    if (leftArrowEnabled()) {
      setMinDisplayIndex(Math.max(0, minDisplayIndex - MAX_SIZE + 1));
    }
  };

  const onDisbursementClick = (disbursement: DisbursementSummary): void => {
    const { handleDisbursementClickCallback } = props;
    const uniquePaidDateIndex = uniquePaidDateList.findIndex((paidDate) => paidDate === disbursement.paidDate);

    const searchType =
      hubTab === TabType.EARNINGS && currentSearchType
        ? SearchTypeLabel[currentSearchType]
        : SearchType.ADVANCED_SEARCH;

    wa.trackEvent('pageNavigationClick', {
      clicktext: `${hubTab}-${searchType}-Disbursement Picker-${
        disbursement.payMethod === 'DTCC'
      }-${uniquePaidDateIndex}`,
      navigationsection: 'call-to-action',
    });

    setCurrentIndex(disbursement.index);
    const disbursementContentLink = disbursement._links.earnings ?? disbursement._links.adjustments;
    handleDisbursementClickCallback(disbursement._links.disbursement, disbursementContentLink);
  };

  useEffect(() => {
    setCurrentIndex(disbursements.length - 1);
    setMinDisplayIndex(Math.max(0, disbursements.length - MAX_SIZE));
  }, [disbursements]);

  if (!disbursements) {
    return <h3>Loading…</h3>;
  }

  return (
    <div id="DisbursementPicker" className="text-center no-print">
      {disbursements.length === 0 && alert ? (
        <Alert message={alert.message} severity={alert.severity} />
      ) : (
        <ul className="pagination">
          <li data-testid="left-arrow" className={leftArrowEnabled() ? '' : 'disabled'}>
            <a href="# " className="disbursement-nav" onClick={onLeftArrowClick} aria-label="Previous disbursement">
              <em className="fa fa-angle-left disbursement-nav-arrow-em" />
            </a>
          </li>
          {disbursements.slice(minDisplayIndex, minDisplayIndex + MAX_SIZE).map((disbursement) => (
            <li key={disbursement.index} className={currentIndex === disbursement.index ? 'active' : ''}>
              <a
                href="# "
                className="disbursement-nav disbursement-nav-element"
                onClick={(event): void => {
                  event.preventDefault();
                  onDisbursementClick(disbursement);
                }}
              >
                <div>{disbursement.payeeName}</div>
                <hr className="line" />
                <div>{Formatter.isoToLongDate(disbursement.paidDate)}</div>
                {(isOps || (basicSearchOption === MY_PAY && currentSearchType === 'basic')) && (
                  <>
                    <hr className="line" />
                    <div>${Formatter.numberToUSD(disbursement.compensationAmount)}</div>
                  </>
                )}
                <hr className="line" />
                {!hidePayMethod && (
                  <div className="pay-method">
                    {disbursement.payMethod === 'Direct Deposit' ? 'Direct Dep' : disbursement.payMethod}
                  </div>
                )}
              </a>
            </li>
          ))}
          <li data-testid="right-arrow" className={rightArrowEnabled() ? '' : 'disabled'}>
            <a href="# " className="disbursement-nav" onClick={onRightArrowClick} aria-label="Next disbursement">
              <em className="fa fa-angle-right disbursement-nav-arrow-em" />
            </a>
          </li>
        </ul>
      )}
    </div>
  );
};

export default DisbursementPicker;
